import { useEffect, useState } from "react";
import { api } from "../../api/client";
import {
  PageTitle, Button, Icon, DataCard, Th, Td, Spinner, cx
} from "../../components/ui";

// ─── Pemantauan Sistem (super admin) ────────────────────────────────────────
// Halaman ini menjawab satu pertanyaan: "ada yang diam-diam rusak, tidak?"
// Tiap baris pemeriksaan adalah pertanyaan yang jawabannya HARUS NOL. Hijau = tak
// perlu dilihat. Bukan nol = ada daftar contohnya untuk ditindaklanjuti.
// Baris bertingkat "info" wajar tidak nol — hanya perlu diketahui.

const GAYA = {
  bahaya: {
    label: "Perlu ditangani",
    icon: "error",
    kotak: "border-rose-500/30 bg-rose-500/5",
    angka: "bg-rose-500/15 text-rose-600 dark:text-rose-400"
  },
  perhatian: {
    label: "Perlu dilihat",
    icon: "warning",
    kotak: "border-amber-500/30 bg-amber-500/5",
    angka: "bg-amber-500/15 text-amber-600 dark:text-amber-400"
  },
  info: {
    label: "Sekadar info",
    icon: "info",
    kotak: "border-slate-300/40 bg-slate-500/5",
    angka: "bg-slate-500/15 text-slate-500 dark:text-slate-400"
  },
  aman: {
    label: "Aman",
    icon: "check_circle",
    kotak: "border-emerald-500/25 bg-emerald-500/5",
    angka: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
  },
  gagal: {
    label: "Gagal diperiksa",
    icon: "help",
    kotak: "border-slate-300/40 bg-slate-500/5",
    angka: "bg-slate-500/15 text-slate-500"
  }
};

const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

// Jarak hari dihitung terhadap "hari ini" MENURUT SERVER (tanggal terakhir pada pola),
// bukan menurut jam di HP pembaca — kalau tidak, pemantau di zona/tanggal berbeda akan
// melihat "kemarin" untuk hal yang bagi server terjadi hari ini.
const sebutTanggal = (iso, hariIni) => {
  if (!iso) return "belum ada";
  const n = Math.round((Date.parse(hariIni) - Date.parse(iso)) / 86400000);
  if (n <= 0) return "hari ini";
  if (n === 1) return "kemarin";
  return `${n} hari lalu`;
};

// Pola 14 hari. Tingginya relatif terhadap hari tersibuk CABANG ITU SENDIRI, bukan
// terhadap cabang terbesar — yang dicari di sini pola berhentinya sebuah cabang, bukan
// perbandingan antar cabang (itu sudah ada di kolom angkanya).
function Pola({ nilai, tanggal }) {
  const maks = Math.max(1, ...nilai);
  return (
    <div className="flex h-7 items-end gap-[3px]">
      {nilai.map((n, i) => (
        <div
          key={i}
          title={`${tanggal[i]} — ${n} kelas diabsen`}
          className={cx("w-1.5 shrink-0 rounded-sm", n ? "bg-primary/70" : "bg-slate-300 dark:bg-slate-700")}
          style={{ height: n ? `${Math.max(14, (n / maks) * 100)}%` : "3px" }}
        />
      ))}
    </div>
  );
}

function BarisPeriksa({ p }) {
  const [buka, setBuka] = useState(false);
  // Nol pada pemeriksaan biasa = aman. Pemeriksaan "info" tetap netral walau nol,
  // supaya hijau benar-benar berarti "tak ada yang perlu dikerjakan".
  const tingkat = p.tingkat === "gagal" ? "gagal"
    : p.jumlah === 0 ? (p.tingkat === "info" ? "info" : "aman")
      : p.tingkat;
  const g = GAYA[tingkat] || GAYA.info;
  const adaIsi = (p.contoh || []).length > 0;

  return (
    <div className={cx("rounded-xl border p-3", g.kotak)}>
      <div className="flex items-start gap-3">
        <Icon name={g.icon} className="mt-0.5 text-base" />
        <div className="min-w-0 flex-1">
          <p className="font-bold leading-snug">{p.judul}</p>
          {p.jumlah !== 0 && p.saran && (
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{p.saran}</p>
          )}
          {p.galat && (
            <p className="mt-1 text-[11px] text-slate-500">Tak bisa diperiksa: {p.galat}</p>
          )}
          {buka && adaIsi && (
            <ul className="mt-2 space-y-1">
              {p.contoh.map((x, i) => (
                <li key={i} className="font-mono text-[11px] text-slate-600 dark:text-slate-300">• {x}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={cx("rounded-lg px-2.5 py-1 text-sm font-black", g.angka)}>
            {p.jumlah === null ? "?" : p.jumlah}
          </span>
          {adaIsi && (
            <button
              onClick={() => setBuka((v) => !v)}
              className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-bold text-slate-500 transition hover:border-primary hover:text-primary dark:border-slate-700"
            >
              {buka ? "Tutup" : "Lihat"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Absensi Harian ─────────────────────────────────────────────────────────
// Isi utama halaman ini, dan sengaja cuma menjawab satu pertanyaan: pada tanggal
// ini, kelas mana yang sudah diabsen dan mana yang belum. TANPA persentase —
// persentase itu urusan admin cabang; yang dipantau di sini cuma sudah/belum,
// karena hanya itu yang bisa ditindaklanjuti hari itu juga.
function BarisKelas({ k }) {
  return (
    <div className="flex items-center gap-2.5 border-t border-slate-200 px-3 py-2 dark:border-slate-800">
      <Icon
        name={k.diabsen ? "check_circle" : "radio_button_unchecked"}
        className={cx("text-base", k.diabsen ? "text-emerald-500" : "text-rose-500")}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold">{k.sesi}</p>
        <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
          {k.trainer || "belum ada trainer"} &middot; {k.murid} murid
        </p>
      </div>
      <span
        className={cx(
          "shrink-0 rounded-lg px-2 py-0.5 text-[10px] font-black uppercase",
          k.diabsen
            ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
            : "bg-rose-500/15 text-rose-600 dark:text-rose-400"
        )}
      >
        {k.diabsen ? "sudah" : "belum"}
      </span>
    </div>
  );
}

function HarianPanel({ setToast }) {
  const [tgl, setTgl] = useState("");        // "" = hari ini MENURUT SERVER
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buka, setBuka] = useState({});

  async function muat(t) {
    setLoading(true);
    try {
      const res = await api.getDailyAttendance(t || undefined);
      if (res?.success) setData(res);
      else setToast && setToast({ type: "error", message: res?.message || "Gagal memuat absensi harian" });
    } catch (err) {
      setToast && setToast({ type: "error", message: err.message || "Gagal memuat absensi harian" });
    } finally { setLoading(false); }
  }

  useEffect(() => { muat(tgl); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tgl]);

  // Geser tanggal tanpa melewati zona waktu: dihitung dari bagian tanggalnya saja.
  function geser(n) {
    const dasar = data?.tanggal;
    if (!dasar) return;
    const [y, m, d] = dasar.split("-").map(Number);
    const t = new Date(y, m - 1, d + n);
    const p = (x) => String(x).padStart(2, "0");
    setTgl(`${t.getFullYear()}-${p(t.getMonth() + 1)}-${p(t.getDate())}`);
  }

  const t = data?.total;
  const adaMasalah = !!(t && t.belum > 0);

  return (
    <div className="mb-6">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button variant="secondary" onClick={() => geser(-1)} disabled={loading || !data}>
          <Icon name="chevron_left" />
        </Button>
        <input
          type="date"
          value={data?.tanggal || ""}
          onChange={(e) => setTgl(e.target.value)}
          className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        />
        <Button variant="secondary" onClick={() => geser(1)} disabled={loading || !data}>
          <Icon name="chevron_right" />
        </Button>
        {data && !data.hariIni && (
          <Button variant="secondary" onClick={() => setTgl("")}>Hari ini</Button>
        )}
        <span className="text-sm font-bold text-slate-500 dark:text-slate-400">
          {data?.hari || ""}{data?.hariIni ? " (hari ini)" : ""}
        </span>
      </div>

      {loading ? (
        <Spinner label="Memuat..." />
      ) : !data ? null : (
        <>
          <div
            className={cx(
              "mb-3 flex items-center gap-3 rounded-xl border p-3 text-sm",
              adaMasalah
                ? "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-400"
                : "border-emerald-500/25 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400"
            )}
          >
            <Icon name={adaMasalah ? "error" : "check_circle"} className="text-base" />
            <p className="flex-1">
              {adaMasalah
                ? <><b>{t.belum} kelas</b> belum diabsen, dari {t.dijadwalkan} kelas yang jadwalnya hari ini.</>
                : t.dijadwalkan
                  ? <><b>Semua {t.dijadwalkan} kelas</b> yang jadwalnya hari ini sudah diabsen.</>
                  : <b>Tidak ada kelas yang jadwalnya hari ini.</b>}
            </p>
          </div>

          {!!t.belumDiatur && (
            <div className="mb-3 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400">
              <Icon name="edit_calendar" className="mt-0.5 text-base" />
              <p>
                <b>{t.belumDiatur} kelas belum diisi hari jalannya</b>, jadi belum bisa ikut
                dipantau di sini. Itu bukan berarti kelasnya libur. Admin cabang mengisinya
                lewat Manajemen Kelas &rarr; Kelola &rarr; &ldquo;Hari kelas ini jalan&rdquo;.
              </p>
            </div>
          )}

          <div className="space-y-2">
            {data.cabang.map((b) => {
              const terbuka = !!buka[b.code];
              return (
                <div key={b.code} className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
                  <button
                    onClick={() => setBuka({ ...buka, [b.code]: !terbuka })}
                    className="flex w-full items-center gap-3 bg-white p-3 text-left transition hover:bg-slate-50 dark:bg-slate-900/40 dark:hover:bg-slate-800/50"
                  >
                    <Icon name={terbuka ? "expand_less" : "expand_more"} className="text-base text-slate-400" />
                    <div className="min-w-0 flex-1">
                      <p className="font-black">
                        {b.code}
                        <span className="ml-2 text-[11px] font-normal text-slate-400">{b.name}</span>
                      </p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        {b.dijadwalkan
                          ? `${b.sudah} dari ${b.dijadwalkan} kelas sudah diabsen`
                          : "tidak ada kelas berjadwal hari ini"}
                        {b.belumDiatur ? ` \u00b7 ${b.belumDiatur} belum berjadwal` : ""}
                      </p>
                    </div>
                    {b.belum > 0 && (
                      <span className="shrink-0 rounded-lg bg-rose-500/15 px-2.5 py-1 text-sm font-black text-rose-600 dark:text-rose-400">
                        {b.belum} belum
                      </span>
                    )}
                    {b.belum === 0 && b.dijadwalkan > 0 && (
                      <Icon name="check_circle" className="shrink-0 text-base text-emerald-500" />
                    )}
                  </button>

                  {terbuka && (
                    <div className="bg-slate-50/60 dark:bg-slate-900/20">
                      {b.kelas.map((k) => <BarisKelas key={k.id} k={k} />)}
                      {!b.kelas.length && (
                        <p className="border-t border-slate-200 px-3 py-2 text-[11px] text-slate-400 dark:border-slate-800">
                          Tidak ada kelas yang jadwalnya hari ini.
                        </p>
                      )}

                      {!!b.luarJadwal.length && (
                        <>
                          <p className="border-t border-slate-200 px-3 pt-2 text-[10px] font-black uppercase tracking-wider text-slate-400 dark:border-slate-800">
                            Diabsen padahal jadwalnya bukan hari ini ({b.luarJadwal.length})
                          </p>
                          {b.luarJadwal.map((k) => <BarisKelas key={"x" + k.id} k={k} />)}
                        </>
                      )}

                      {!!b.takTerjadwal.length && (
                        <>
                          <p className="border-t border-slate-200 px-3 pt-2 text-[10px] font-black uppercase tracking-wider text-amber-600 dark:border-slate-800 dark:text-amber-400">
                            Belum diisi hari jalannya ({b.takTerjadwal.length})
                          </p>
                          {b.takTerjadwal.map((k) => <BarisKelas key={"n" + k.id} k={k} />)}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Fase 2c-3 (c): banding coverage nama vs akun ───────────────────────────
// Alat kesiapan, bukan pemantauan harian — karena itu ia ikut terlipat bersama
// pemeriksaan lain. Yang dibandingkan angka yang masuk ke rumus gaji, jadi "cocok"
// di sini berarti menukar sumber nama tidak akan menggeser rupiah siapa pun.
function BandingCoverage({ setToast }) {
  const kini = new Date();
  const [bulan, setBulan] = useState(
    `${kini.getFullYear()}-${String(kini.getMonth() + 1).padStart(2, "0")}`
  );
  const [hasil, setHasil] = useState(null);
  const [sibuk, setSibuk] = useState(false);

  async function jalankan() {
    setSibuk(true); setHasil(null);
    try {
      const [y, m] = bulan.split("-");
      const res = await api.compareCoverage({ year: Number(y), month: Number(m) });
      if (res?.success) setHasil(res);
      else setToast && setToast({ type: "error", message: res?.message || "Gagal membandingkan" });
    } catch (err) {
      setToast && setToast({ type: "error", message: err.message || "Gagal membandingkan" });
    } finally { setSibuk(false); }
  }

  return (
    <div className="mb-6 rounded-xl border border-slate-200 p-3 dark:border-slate-800">
      <p className="text-sm font-black">Banding pergantian trainer: nama vs akun</p>
      <p className="mb-3 mt-1 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
        Menghitung masukan gaji bulan itu <b>dua kali</b> &mdash; sekali lewat nama yang
        tertulis, sekali lewat akun yang ditunjuk &mdash; lalu melaporkan tiap hari yang
        angkanya berbeda. Dipakai untuk memastikan perpindahan ke akun tidak menggeser
        gaji siapa pun. Hanya membaca, tak ada yang diubah.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="month"
          value={bulan}
          onChange={(e) => setBulan(e.target.value)}
          className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        />
        <Button variant="secondary" onClick={jalankan} disabled={sibuk || !bulan}>
          <Icon name="difference" />
          {sibuk ? "Membandingkan..." : "Bandingkan"}
        </Button>
      </div>

      {hasil && (
        <div className="mt-3">
          <div
            className={cx(
              "flex items-center gap-2 rounded-lg border p-2.5 text-xs",
              hasil.cocok
                ? "border-emerald-500/25 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400"
                : "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-400"
            )}
          >
            <Icon name={hasil.cocok ? "check_circle" : "error"} className="text-base" />
            <p className="flex-1">
              {hasil.cocok
                ? <>Sama persis. {hasil.diperiksa.kunciViaNama} kelompok gaji diperiksa &mdash; aman dipindah ke akun.</>
                : <><b>{hasil.jumlahBaris} kelompok</b> berbeda, {hasil.jumlahHari} hari. Jangan dipindah dulu.</>}
            </p>
          </div>

          {!hasil.cocok && (
            <div className="mt-2 space-y-1">
              {hasil.beda.map((b, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-2 text-[11px] dark:border-slate-800">
                  <p className="font-bold">
                    <span className="text-slate-400">BR{String(b.cabang).padStart(2, "0")}</span>{" "}
                    {b.trainer || "(tanpa nama)"} &middot; {b.sesi}
                    <span className="ml-2 rounded bg-slate-500/15 px-1.5 py-0.5 font-black text-slate-500">
                      {b.adaDiNama && !b.adaDiId ? "hanya lewat nama"
                        : !b.adaDiNama && b.adaDiId ? "hanya lewat akun"
                          : "angkanya beda"}
                    </span>
                  </p>
                  <p className="mt-0.5 font-mono text-slate-500 dark:text-slate-400">
                    {b.hari.map((h) => `tgl ${h.tanggal}: ${h.viaNama} \u2192 ${h.viaId}`).join("  ·  ")}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MonitorPage({ setToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Pemeriksaan "teknis" (kerapian data) disembunyikan secara bawaan: pemantau harian
  // bukan orang IT, dan daftar yang terlalu panjang justru berhenti dibaca. Tetap ada,
  // tinggal dibuka — dan otomatis terbuka sendiri bila ada yang tak nol.
  const [bukaTeknis, setBukaTeknis] = useState(false);
  // Seluruh pemeriksaan kesehatan data dilipat: yang dipantau tiap hari cuma absensi.
  const [bukaLain, setBukaLain] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await api.getSystemHealth();
      if (res?.success) setData(res);
      else setToast && setToast({ type: "error", message: res?.message || "Gagal memuat pemantauan" });
    } catch (err) {
      setToast && setToast({ type: "error", message: err.message || "Gagal memuat pemantauan" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Urutan: yang perlu ditangani dulu, yang aman paling bawah.
  const urut = (arr) => {
    const bobot = (p) => {
      if (p.tingkat === "gagal") return 1;
      if (p.jumlah === 0) return 4;
      if (p.tingkat === "bahaya") return 0;
      if (p.tingkat === "perhatian") return 2;
      return 3;
    };
    return [...arr].sort((a, b) => bobot(a) - bobot(b));
  };

  const periksa = data?.periksa || [];
  const operasional = periksa.filter((p) => p.kategori !== "teknis");
  const teknis = periksa.filter((p) => p.kategori === "teknis");
  const teknisBermasalah = teknis.filter((p) => p.jumlah > 0).length;
  const perluTindak = operasional.filter((p) => p.jumlah > 0 && p.tingkat !== "info").length;
  const absensi = data?.absensi || null;
  const coverage = data?.coverage || null;
  // "Hari ini" diambil dari tanggal terakhir pola (menurut server), bukan dari jam HP.
  const hariIni = absensi?.tanggal?.[absensi.tanggal.length - 1] || null;
  const ringkasan = data?.ringkasan || [];
  const total = ringkasan.reduce(
    (a, b) => ({
      muridAktif: a.muridAktif + b.muridAktif,
      kelas: a.kelas + b.kelas,
      akun: a.akun + b.akun,
      gajiBulanIni: a.gajiBulanIni + b.gajiBulanIni
    }),
    { muridAktif: 0, kelas: 0, akun: 0, gajiBulanIni: 0 }
  );

  return (
    <>
      <PageTitle
        title="Pemantauan Absensi"
        subtitle="Kelas mana yang sudah diabsen hari ini, di cabang mana."
      />

      <HarianPanel setToast={setToast} />

      {/* Sisanya dilipat: berguna, tapi bukan pekerjaan harian pemantau. Dibuka
          hanya kalau memang sedang mencari sesuatu. */}
      <button
        onClick={() => setBukaLain((v) => !v)}
        className="mb-3 flex w-full items-center gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-2 text-xs font-bold text-slate-500 transition hover:border-primary hover:text-primary dark:border-slate-700"
      >
        <Icon name={bukaLain ? "expand_less" : "expand_more"} className="text-sm" />
        Pemeriksaan lain
        <span className="ml-auto font-normal opacity-70">
          kesehatan data seluruh cabang &mdash; buka bila perlu
        </span>
      </button>

      {bukaLain && (
      <>
      <div className="mb-3 flex justify-end">
        <Button variant="secondary" onClick={load} disabled={loading}>
          <Icon name="refresh" />
          Periksa Ulang
        </Button>
      </div>

      {loading ? (
        <Spinner label="Memeriksa..." />
      ) : !data ? null : (
        <>
          <div
            className={cx(
              "mb-4 flex items-center gap-3 rounded-xl border p-3 text-sm",
              perluTindak
                ? "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-400"
                : "border-emerald-500/25 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400"
            )}
          >
            <Icon name={perluTindak ? "error" : "check_circle"} className="text-base" />
            <p className="flex-1">
              {perluTindak
                ? <><b>{perluTindak} hal</b> perlu ditindaklanjuti.</>
                : <b>Semuanya beres — tidak ada yang perlu dikerjakan.</b>}
            </p>
            <span className="text-[11px] opacity-70">
              {data.diperiksa ? new Date(data.diperiksa).toLocaleString("id-ID") : ""}
            </span>
          </div>

          <div className="mb-4 space-y-2">
            {urut(operasional).map((p) => <BarisPeriksa key={p.kode} p={p} />)}
          </div>

          {!!teknis.length && (
            <div className="mb-6">
              <button
                onClick={() => setBukaTeknis((v) => !v)}
                className="flex w-full items-center gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-2 text-xs font-bold text-slate-500 transition hover:border-primary hover:text-primary dark:border-slate-700"
              >
                <Icon name={bukaTeknis ? "expand_less" : "expand_more"} className="text-sm" />
                Pemeriksaan kerapian data ({teknis.length})
                {!!teknisBermasalah && (
                  <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-black text-amber-600">
                    {teknisBermasalah} perlu dilihat
                  </span>
                )}
                <span className="ml-auto font-normal opacity-70">
                  bukan pekerjaan harian — buka bila perlu
                </span>
              </button>
              {(bukaTeknis || teknisBermasalah > 0) && (
                <div className="mt-2 space-y-2">
                  {urut(teknis).map((p) => <BarisPeriksa key={p.kode} p={p} />)}
                </div>
              )}
            </div>
          )}

          {absensi && (
            <>
              <p className="mb-1 text-xs font-black uppercase tracking-wider text-slate-400">
                Absensi per cabang
              </p>
              <p className="mb-2 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                Sistem tidak menyimpan jadwal hari tiap kelas, jadi di sini tidak ada persentase
                &ldquo;seharusnya berapa kali&rdquo;. Yang ditampilkan apa adanya: kelas mana yang
                sudah tersentuh absensi bulan ini, dan kapan terakhir ada absensi. Batang kecilnya
                adalah 14 hari terakhir &mdash; yang perlu dicurigai bukan hari kosongnya, tapi
                cabang yang tiba-tiba berhenti.
              </p>

              <DataCard className="hidden md:block">
                <table className="w-full min-w-[720px] whitespace-nowrap text-left">
                  <thead>
                    <tr>
                      <Th>CABANG</Th>
                      <Th right>KELAS</Th>
                      <Th right>SUDAH DIABSEN</Th>
                      <Th right>BELUM</Th>
                      <Th center>14 HARI TERAKHIR</Th>
                      <Th right>ABSENSI TERAKHIR</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {absensi.perCabang.map((b) => (
                      <tr key={b.code} className="border-t border-slate-200 dark:border-slate-800">
                        <Td>
                          <span className="font-black">{b.code}</span>
                          <span className="ml-2 text-[11px] text-slate-400">{b.name}</span>
                        </Td>
                        <Td right>{b.kelasAktif}</Td>
                        <Td right>{b.diabsen}</Td>
                        <Td right>
                          <span className={cx("font-black", b.belum ? "text-amber-600 dark:text-amber-400" : "text-slate-400")}>
                            {b.belum}
                          </span>
                        </Td>
                        <Td center>
                          <div className="flex justify-center">
                            <Pola nilai={b.pola} tanggal={absensi.tanggal} />
                          </div>
                        </Td>
                        <Td right>{sebutTanggal(b.terakhir, hariIni)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DataCard>

              <div className="space-y-2 md:hidden">
                {absensi.perCabang.map((b) => (
                  <div key={b.code} className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/40">
                    <div className="flex items-baseline justify-between">
                      <p className="font-black">{b.code}</p>
                      <p className="text-[11px] text-slate-400">{sebutTanggal(b.terakhir, hariIni)}</p>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
                      {[["Kelas", b.kelasAktif, ""], ["Diabsen", b.diabsen, ""],
                        ["Belum", b.belum, b.belum ? "text-amber-600" : ""]].map(([l, v, w]) => (
                        <div key={l} className="rounded-lg bg-slate-100 py-1.5 dark:bg-slate-800">
                          <p className="text-[10px] font-bold uppercase text-slate-400">{l}</p>
                          <p className={cx("font-black", w)}>{v}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex justify-center">
                      <Pola nilai={b.pola} tanggal={absensi.tanggal} />
                    </div>
                  </div>
                ))}
              </div>

              {(!!absensi.kelasSepi.length || !!absensi.kelasBaru) && (
                <>
                  <p className="mb-1 mt-5 text-xs font-black uppercase tracking-wider text-slate-400">
                    Kelas yang lama tidak diabsen
                  </p>
                  <p className="mb-2 text-[11px] text-slate-500 dark:text-slate-400">
                    {absensi.kelasBaru
                      ? `${absensi.kelasBaru} kelas yang baru dibuat seminggu terakhir sengaja belum dihitung di daftar ini.`
                      : "Kelas yang baru dibuat seminggu terakhir tidak ikut dihitung."}
                    {absensi.kelasSepiTotal > absensi.kelasSepi.length
                      ? ` Ditampilkan ${absensi.kelasSepi.length} teratas dari ${absensi.kelasSepiTotal}.`
                      : ""}
                  </p>
                  {!absensi.kelasSepi.length && (
                    <p className="rounded-xl border border-slate-300/40 bg-slate-500/5 p-3 text-xs text-slate-500 dark:text-slate-400">
                      Belum ada yang bisa ditelusuri di sini &mdash; semua kelas masih dalam masa
                      kelonggaran itu. Angka di tabel atas tetap apa adanya.
                    </p>
                  )}
                  <div className="space-y-1.5">
                    {absensi.kelasSepi.map((k, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-2.5 dark:border-slate-800 dark:bg-slate-900/40"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold">
                            <span className="text-slate-400">{k.cabang}</span> {k.sesi}
                          </p>
                          <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                            {k.trainer || "belum ada trainer"} &middot; {k.murid} murid
                          </p>
                        </div>
                        <span
                          className={cx(
                            "shrink-0 rounded-lg px-2 py-1 text-[11px] font-black",
                            k.murid === 0
                              ? "bg-slate-500/15 text-slate-500"
                              : "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                          )}
                        >
                          {k.hari === null ? "belum pernah" : `${k.hari} hari`}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {!!(coverage && coverage.length) && (
            <>
              <p className="mb-1 mt-5 text-xs font-black uppercase tracking-wider text-slate-400">
                Pergantian trainer bulan ini
              </p>
              <p className="mb-2 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                <b>Pergantian</b> = kelas diajar trainer lain. <b>Penggabungan</b> = dua kelas
                disatukan jadi satu. Yang <b>belum dikunci</b> belum berlaku di mana pun: trainer
                penggantinya tak melihat kelasnya dan belum dihitung gajinya.
              </p>
              <DataCard className="hidden md:block">
                <table className="w-full min-w-[520px] whitespace-nowrap text-left">
                  <thead>
                    <tr>
                      <Th>CABANG</Th>
                      <Th right>PERGANTIAN</Th>
                      <Th right>PENGGABUNGAN</Th>
                      <Th right>BELUM DIKUNCI</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {coverage.map((b) => (
                      <tr key={b.code} className="border-t border-slate-200 dark:border-slate-800">
                        <Td>
                          <span className="font-black">{b.code}</span>
                          <span className="ml-2 text-[11px] text-slate-400">{b.name}</span>
                        </Td>
                        <Td right>{b.substitusi}</Td>
                        <Td right>{b.gabung}</Td>
                        <Td right>
                          <span className={cx("font-black", b.draft ? "text-rose-600 dark:text-rose-400" : "text-slate-400")}>
                            {b.draft}
                          </span>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DataCard>
              <div className="space-y-2 md:hidden">
                {coverage.map((b) => (
                  <div key={b.code} className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/40">
                    <p className="font-black">{b.code}</p>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
                      {[["Pergantian", b.substitusi, ""], ["Penggabungan", b.gabung, ""],
                        ["Belum dikunci", b.draft, b.draft ? "text-rose-600" : ""]].map(([l, v, w]) => (
                        <div key={l} className="rounded-lg bg-slate-100 py-1.5 dark:bg-slate-800">
                          <p className="text-[10px] font-bold uppercase leading-tight text-slate-400">{l}</p>
                          <p className={cx("font-black", w)}>{v}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <BandingCoverage setToast={setToast} />

          <p className="mb-2 mt-5 text-xs font-black uppercase tracking-wider text-slate-400">
            Ringkasan per cabang
          </p>
          <DataCard className="hidden md:block">
            <table className="w-full min-w-[640px] whitespace-nowrap text-left">
              <thead>
                <tr>
                  {["CABANG", "MURID AKTIF", "KELAS", "AKUN", "GAJI BULAN INI"].map((h, i) => (
                    <Th key={h} right={i > 0}>{h}</Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ringkasan.map((b) => (
                  <tr key={b.id} className="border-t border-slate-200 dark:border-slate-800">
                    <Td>
                      <span className="font-black">{b.code}</span>
                      <span className="ml-2 text-[11px] text-slate-400">{b.name}</span>
                    </Td>
                    <Td right>{b.muridAktif}</Td>
                    <Td right>{b.kelas}</Td>
                    <Td right>{b.akun}</Td>
                    <Td right mono>{rupiah(b.gajiBulanIni)}</Td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-300 font-black dark:border-slate-700">
                  <Td>TOTAL</Td>
                  <Td right>{total.muridAktif}</Td>
                  <Td right>{total.kelas}</Td>
                  <Td right>{total.akun}</Td>
                  <Td right mono>{rupiah(total.gajiBulanIni)}</Td>
                </tr>
              </tbody>
            </table>
          </DataCard>

          <div className="space-y-2 md:hidden">
            {ringkasan.map((b) => (
              <div key={b.id} className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/40">
                <div className="flex items-baseline justify-between">
                  <p className="font-black">{b.code}</p>
                  <p className="text-[11px] text-slate-400">{b.name}</p>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
                  {[["Murid", b.muridAktif], ["Kelas", b.kelas], ["Akun", b.akun]].map(([l, v]) => (
                    <div key={l} className="rounded-lg bg-slate-100 py-1.5 dark:bg-slate-800">
                      <p className="text-[10px] font-bold uppercase text-slate-400">{l}</p>
                      <p className="font-black">{v}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-right font-mono text-xs font-bold">{rupiah(b.gajiBulanIni)}</p>
              </div>
            ))}
          </div>
        </>
      )}
      </>
      )}
    </>
  );
}
